import React, { useState, useEffect, useCallback, useRef } from 'react';
import { submitForm } from '../services/formSubmissionService';
import { getPhotoStudioAppointments } from '../services/photoStudioService';
import { sendBookingConfirmationEmail } from '../services/emailService';
import queueService from '../services/dynamoQueueService';
import { generateSequentialToken, calculateEstimatedTime } from '../utils/tokenGenerator';
import BookingImageGenerator from './BookingImageGenerator';
import ErrorBoundary from './ErrorBoundary';
import { getHolidays } from '../services/holidayService';
import socketService from '../services/socketService';
import InputValidator from '../utils/inputValidation';

import './PhotoStudio.css';

function PhotoStudioFormEnhanced() {
  // Enhanced state management with error tracking
  const [formState, setFormState] = useState({
    email: '',
    selectedStudio: '',
    selectedSlot: '',
    selectedDate: new Date().toISOString().split('T')[0],
    showDatePicker: false
  });

  const [validationState, setValidationState] = useState({
    emailError: '',
    formErrors: [],
    warnings: []
  });

  const [submissionState, setSubmissionState] = useState({
    isSubmitting: false,
    submitStatus: null,
    bookingError: '',
    retryCount: 0,
    lastAttemptTime: null
  });

  const [dataState, setDataState] = useState({
    slotsData: {},
    futureDateSlots: {},
    holidays: [],
    existingBooking: null,
    bookingDetails: null,
    bookingImageUrl: null,
    isLoading: false,
    lastRefresh: null
  });

  // Refs for cleanup and debouncing
  const abortControllerRef = useRef(null);
  const validationTimeoutRef = useRef(null);
  const refreshIntervalRef = useRef(null);

  // Enhanced time slots with validation
  const TIME_SLOTS = [
    { id: '10:00-11:00', label: '10:00 AM - 11:00 AM', maxBookings: 20 },
    { id: '11:00-12:00', label: '11:00 AM - 12:00 PM', maxBookings: 20 },
    { id: '12:00-13:00', label: '12:00 PM - 1:00 PM', maxBookings: 20 },
    { id: '13:00-14:00', label: '1:00 PM - 2:00 PM', maxBookings: 20 },
    { id: '15:00-16:00', label: '3:00 PM - 4:00 PM', maxBookings: 20 },
    { id: '16:00-17:00', label: '4:00 PM - 5:00 PM', maxBookings: 20 }
  ];

  // Enhanced email validation with real-time feedback
  const handleEmailChange = useCallback((value) => {
    setFormState(prev => ({ ...prev, email: value }));
    
    // Clear previous timeout
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }
    
    // Debounced validation
    validationTimeoutRef.current = setTimeout(() => {
      const validation = InputValidator.validateEmail(value);
      setValidationState(prev => ({
        ...prev,
        emailError: validation.valid ? '' : validation.error
      }));
    }, 300);
  }, []);

  // Enhanced form validation with detailed feedback
  const validateForm = useCallback(() => {
    const errors = [];
    const warnings = [];

    if (!formState.email) {
      errors.push('Email is required');
    } else if (validationState.emailError) {
      errors.push(validationState.emailError);
    }

    if (!formState.selectedStudio) {
      errors.push('Please select a studio location');
    }

    if (!formState.selectedSlot) {
      errors.push('Please select a time slot');
    }

    // Date validation
    const dateValidation = InputValidator.validateDate(formState.selectedDate);
    if (!dateValidation.valid) {
      errors.push(dateValidation.error);
    }

    // Check if selected date is a holiday
    if (dataState.holidays.includes(formState.selectedDate)) {
      errors.push('Studio is closed on the selected date (Holiday)');
    }

    setValidationState(prev => ({
      ...prev,
      formErrors: errors,
      warnings
    }));

    return errors.length === 0;
  }, [formState, validationState.emailError, dataState.holidays]);

  // Enhanced slot availability check with caching
  const checkSlotAvailabilityForDate = useCallback(async (studio, slotId, date) => {
    try {
      // Cancel previous request if still pending
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      abortControllerRef.current = new AbortController();
      
      const result = await getPhotoStudioAppointments();
      if (result.success) {
        const bookings = result.data.filter(b => 
          b.siteCode === studio && 
          new Date(b.submissionDate).toDateString() === new Date(date).toDateString() &&
          b.estimatedTime?.includes(slotId) &&
          b.status !== 'Cancelled' &&
          b.status !== 'Expired'
        );
        
        return { 
          available: bookings.length < 20, 
          bookedCount: bookings.length,
          maxSlots: 20,
          nextPosition: bookings.length + 1
        };
      }
      return { available: true, bookedCount: 0, maxSlots: 20, nextPosition: 1 };
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Request aborted');
        return null;
      }
      console.error('Slot availability check failed:', error);
      return { available: true, bookedCount: 0, maxSlots: 20, nextPosition: 1 };
    }
  }, []);

  // Enhanced slots data update with error handling
  const updateSlotsData = useCallback(async () => {
    if (!formState.selectedStudio) return;
    
    setDataState(prev => ({ ...prev, isLoading: true }));
    
    try {
      const newSlotsData = { [formState.selectedStudio]: {} };
      const selectedDate = formState.selectedDate;
      
      // Use Redis for instant slot counts — no DynamoDB scan needed
      const counts = await Promise.all(
        TIME_SLOTS.map(async (slot) => {
          const count = await queueService.getSlotCount(formState.selectedStudio, selectedDate, slot.id);
          return { slotId: slot.id, count };
        })
      );
      
      counts.forEach(({ slotId, count }) => {
        newSlotsData[formState.selectedStudio][slotId] = {
          bookedCount: count,
          available: count < 20,
          lastUpdated: new Date().toISOString()
        };
      });
      
      setDataState(prev => ({ 
        ...prev, 
        slotsData: newSlotsData,
        lastRefresh: new Date().toISOString(),
        isLoading: false
      }));
    } catch (error) {
      console.error('Error updating slots data:', error);
      setDataState(prev => ({ ...prev, isLoading: false }));
    }
  }, [formState.selectedStudio, formState.selectedDate]);

  // Enhanced existing booking check with debouncing
  const checkExistingBooking = useCallback(async (userEmail) => {
    try {
      const result = await getPhotoStudioAppointments();
      
      if (result.success && result.data) {
        const today = new Date().toDateString();
        const userBooking = result.data.find(booking => 
          booking.email === userEmail &&
          new Date(booking.submissionDate).toDateString() === today &&
          (booking.status === 'Pending' || booking.status === 'In Progress')
        );
        
        return userBooking || null;
      }
      
      return null;
    } catch (error) {
      console.error('Error checking existing booking:', error);
      return null;
    }
  }, []);

  // Enhanced booking submission with time validation
  const performBooking = useCallback(async () => {
    try {
      console.log('=== ENHANCED BOOKING FLOW START ===');
      
      // Pre-flight validation
      if (!validateForm()) {
        throw new Error('Form validation failed');
      }

      // Time validation for today's bookings
      const selectedDateObj = new Date(formState.selectedDate);
      const isToday = selectedDateObj.toDateString() === new Date().toDateString();
      
      if (isToday) {
        const now = new Date();
        const currentTime = now.getHours() * 60 + now.getMinutes();
        const slotEndTime = parseInt(formState.selectedSlot.split('-')[1].split(':')[0]) * 60 + 
                            parseInt(formState.selectedSlot.split('-')[1].split(':')[1]);
        
        // Only block if the slot has completely ended
        if (currentTime >= slotEndTime) {
          throw new Error('This slot has already ended. Please select a later time slot.');
        }
      }

      // Check for existing booking
      if (dataState.existingBooking) {
        throw new Error(`You already have a pending booking today (Token: ${dataState.existingBooking.tokenNumber})`);
      }

      const selectedDateStr = formState.selectedDate; // Already YYYY-MM-DD from date input

      // Atomic position generation (Redis handles slot-full check too)
      console.log('=== ATOMIC POSITION GENERATION ===');
      const positionResult = await queueService.getAtomicPosition(
        formState.selectedStudio, 
        selectedDateStr, 
        formState.selectedSlot
      );
      
      const actualQueuePosition = positionResult.position;
      
      if (actualQueuePosition > 20) {
        const slotLabel = TIME_SLOTS.find(s => s.id === formState.selectedSlot)?.label;
        throw new Error(`Sorry! The ${slotLabel} slot is now full. Please choose another time slot.`);
      }

      console.log(`✅ Atomic position secured: ${actualQueuePosition}`);

      // Generate booking details with realistic arrival time
      const tokenNumber = generateSequentialToken(formState.selectedStudio, actualQueuePosition);
      const slotStartTime = formState.selectedSlot.split('-')[0];
      
      // Calculate arrival time — always based on slot start + position for consistent 3-min gaps
      // This ensures person #1 gets slot start, #2 gets +3min, #3 gets +6min, etc.
      const arrivalTime = calculateEstimatedTime(actualQueuePosition, slotStartTime);
      const uniqueId = `${formState.selectedStudio}-${selectedDateStr}-${formState.selectedSlot}-${actualQueuePosition}-${Date.now()}`;

      // Prepare form data with validation
      const formData = {
        id: uniqueId,
        email: formState.email,
        siteCode: formState.selectedStudio,
        slotId: formState.selectedSlot,
        queuePosition: actualQueuePosition,
        tokenNumber: tokenNumber,
        estimatedTime: `${arrivalTime}|${formState.selectedSlot}`,
        timeSlot: formState.selectedSlot,
        requestType: 'photoStudio',
        submissionDate: new Date().toLocaleString(),
        status: 'Pending',
        photoTaken: 'No',
        formType: 'photoStudio',
        clientIP: 'browser', // In production, get from server
        userAgent: navigator.userAgent.substring(0, 200)
      };

      // Validate form data before submission
      const validation = InputValidator.validateFormData(formData);
      if (!validation.valid) {
        throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
      }

      console.log('Form Data being submitted:', formData);

      // Submit form
      const submitResult = await submitForm(validation.sanitizedData, 'photoStudio');
      if (!submitResult.success) {
        // Rollback Redis position since save failed
        try {
          await queueService.rollbackPosition(formState.selectedStudio, selectedDateStr, formState.selectedSlot);
        } catch (rollbackErr) {
          console.warn('Position rollback failed:', rollbackErr);
        }
        throw new Error(submitResult.message || 'Form submission failed');
      }

      console.log('✅ Form submission successful');

      // Prepare booking details for display
      const details = {
        tokenNumber: formData.tokenNumber,
        location: formState.selectedStudio,
        timeSlot: TIME_SLOTS.find(s => s.id === formState.selectedSlot)?.label,
        queuePosition: actualQueuePosition,
        arrivalTime: arrivalTime
      };

      setDataState(prev => ({ ...prev, bookingDetails: details }));

      // Send confirmation email (non-blocking)
      try {
        const emailResult = await sendBookingConfirmationEmail(formState.email, details);
        if (emailResult.success) {
          console.log('✅ Booking confirmation email sent successfully');
        } else {
          console.warn('⚠️ Booking confirmation email failed:', emailResult.message);
        }
      } catch (emailError) {
        console.warn('❌ Email service not available:', emailError);
      }

      // Update submission state
      setSubmissionState(prev => ({
        ...prev,
        submitStatus: {
          success: true,
          tokenNumber: formData.tokenNumber,
          arrivalTime: arrivalTime,
          emailSent: true
        },
        isSubmitting: false,
        bookingError: '',
        retryCount: 0
      }));

      // Refresh slots data
      updateSlotsData();

    } catch (error) {
      console.error('Enhanced booking failed:', error);
      
      setSubmissionState(prev => ({
        ...prev,
        bookingError: error.message || 'Booking failed. Please try again.',
        isSubmitting: false,
        retryCount: prev.retryCount + 1,
        lastAttemptTime: new Date().toISOString()
      }));

      // Clear selected slot if it's full
      if (error.message && error.message.includes('slot full')) {
        setFormState(prev => ({ ...prev, selectedSlot: '' }));
      }
    }
  }, [formState, dataState.existingBooking, validateForm, updateSlotsData]);

  // Enhanced form submission with retry logic
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setSubmissionState(prev => ({ ...prev, bookingError: '' }));
    setValidationState(prev => ({ ...prev, formErrors: [], warnings: [] }));

    // Validate form
    if (!validateForm()) {
      return;
    }

    // Check rate limiting
    const rateLimitCheck = InputValidator.validateRateLimit('browser', 'booking');
    if (!rateLimitCheck.allowed) {
      setSubmissionState(prev => ({
        ...prev,
        bookingError: rateLimitCheck.error
      }));
      return;
    }

    setSubmissionState(prev => ({ ...prev, isSubmitting: true }));

    try {
      await performBooking();
    } catch (error) {
      console.error('Form submission failed:', error);
      setSubmissionState(prev => ({
        ...prev,
        bookingError: error.message || 'Submission failed. Please try again.',
        isSubmitting: false
      }));
    }
  }, [validateForm, performBooking]);

  // Enhanced effects with cleanup
  useEffect(() => {
    updateSlotsData();
  }, [formState.selectedStudio, formState.selectedDate, updateSlotsData]);

  useEffect(() => {
    // Auto-refresh slots data every 60 seconds via Redis (lightweight)
    if (formState.selectedStudio) {
      refreshIntervalRef.current = setInterval(updateSlotsData, 60000);
    }
    
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [formState.selectedStudio, formState.selectedDate, updateSlotsData]);

  useEffect(() => {
    // Fetch holidays on component mount
    const fetchHolidays = async () => {
      try {
        const result = await getHolidays();
        if (result.success) {
          setDataState(prev => ({
            ...prev,
            holidays: result.data.map(h => h.date)
          }));
        }
      } catch (error) {
        console.warn('Holiday service not available:', error);
      }
    };
    fetchHolidays();
  }, []);

  useEffect(() => {
    // Check existing booking when email changes
    if (formState.email && !validationState.emailError) {
      const checkBooking = async () => {
        const booking = await checkExistingBooking(formState.email);
        setDataState(prev => ({ ...prev, existingBooking: booking }));
      };
      
      const timeoutId = setTimeout(checkBooking, 1500);
      return () => clearTimeout(timeoutId);
    } else {
      setDataState(prev => ({ ...prev, existingBooking: null }));
    }
  }, [formState.email, validationState.emailError, checkExistingBooking]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  // Success state rendering
  if (submissionState.submitStatus && submissionState.submitStatus.success) {
    return (
      <ErrorBoundary>
        <div className="form-container">
          <BookingImageGenerator 
            bookingDetails={dataState.bookingDetails}
            onImageGenerated={(url) => setDataState(prev => ({ ...prev, bookingImageUrl: url }))}
          />
          <div style={{textAlign: 'center', marginTop: '30px'}}>
            {dataState.bookingImageUrl && dataState.bookingImageUrl !== 'fallback' ? (
              <div>
                <img 
                  src={dataState.bookingImageUrl} 
                  alt={`Booking confirmation for token ${submissionState.submitStatus.tokenNumber}`}
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: '8px',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                  }}
                />
                <div style={{marginTop: '20px'}}>
                  <a 
                    href={dataState.bookingImageUrl}
                    download={`photo-studio-booking-${submissionState.submitStatus.tokenNumber}.png`}
                    style={{
                      backgroundColor: '#ff9900',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      marginRight: '10px',
                      textDecoration: 'none',
                      display: 'inline-block'
                    }}
                  >
                    Download Confirmation
                  </a>
                  <button 
                    onClick={() => window.location.reload()}
                    style={{
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    New Booking
                  </button>
                </div>
              </div>
            ) : (
              <div style={{
                padding: '30px',
                backgroundColor: '#e8f5e8',
                border: '1px solid #c3e6cb',
                borderRadius: '8px',
                color: '#155724'
              }}>
                <h3>Booking Confirmed!</h3>
                <p><strong>Token:</strong> {submissionState.submitStatus.tokenNumber}</p>
                <p><strong>Arrive At:</strong> {submissionState.submitStatus.arrivalTime}</p>
                {submissionState.submitStatus.emailSent && (
                  <div style={{
                    backgroundColor: '#d1ecf1',
                    border: '1px solid #bee5eb',
                    borderRadius: '4px',
                    padding: '10px',
                    margin: '10px 0',
                    fontSize: '14px'
                  }}>
                    📧 <strong>Confirmation email sent!</strong> Check your inbox for booking details.
                  </div>
                )}
                <button 
                  onClick={() => window.location.reload()}
                  style={{
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    marginTop: '15px'
                  }}
                >
                  New Booking
                </button>
              </div>
            )}
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  // Main form rendering
  return (
    <ErrorBoundary>
      <div className="form-container">
        <h2>📸 Photo Studio Appointment</h2>
        
        {/* Enhanced error display */}
        {validationState.formErrors.length > 0 && (
          <div style={{
            color: '#dc3545',
            backgroundColor: '#f8d7da',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            padding: '15px',
            margin: '15px 0'
          }}>
            <div style={{fontWeight: 'bold', marginBottom: '8px'}}>⚠️ Please fix the following errors:</div>
            <ul style={{margin: 0, paddingLeft: '20px'}}>
              {validationState.formErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Warnings display */}
        {validationState.warnings.length > 0 && (
          <div style={{
            color: '#856404',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: '4px',
            padding: '10px',
            margin: '15px 0'
          }}>
            <div style={{fontWeight: 'bold', marginBottom: '5px'}}>⚠️ Warnings:</div>
            <ul style={{margin: 0, paddingLeft: '20px'}}>
              {validationState.warnings.map((warning, index) => (
                <li key={index}>{warning}</li>
              ))}
            </ul>
          </div>
        )}

        <BookingImageGenerator 
          bookingDetails={dataState.bookingDetails}
          onImageGenerated={(url) => setDataState(prev => ({ ...prev, bookingImageUrl: url }))}
        />
      
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="section-label">
              youralias@amazon.com <span className="required">*</span>
            </label>
            <input
              type="email"
              value={formState.email}
              onChange={(e) => handleEmailChange(e.target.value)}
              placeholder="youralias@amazon.com"
              required
              style={{
                width: '60%',
                padding: '12px 15px',
                border: validationState.emailError ? '2px solid #dc3545' : '2px solid #ddd',
                borderRadius: '6px',
                fontSize: '16px',
                marginBottom: '5px'
              }}
            />
            {validationState.emailError && (
              <div style={{color: '#dc3545', fontSize: '14px', marginTop: '5px'}}>
                {validationState.emailError}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="section-label">
              Select Photo Studio Location <span className="required">*</span>
            </label>
            <select 
              className="site-select" 
              value={formState.selectedStudio} 
              onChange={(e) => setFormState(prev => ({ ...prev, selectedStudio: e.target.value }))}
              required
            >
              <option value="">Select Studio Location</option>
              <option value="BLR13">BLR13 Photo Studio (Taurus 1)</option>
              <option value="BLR18">BLR18 Photo Studio (Aquilla)</option>
            </select>
          </div>

          {formState.selectedStudio && (
            <div className="form-group">
              <div style={{marginBottom: '15px'}}>
                {!formState.showDatePicker ? (
                  <div>
                    <p style={{margin: '10px 0', color: '#666'}}>
                      📅 Booking for: <strong>Today ({new Date().toLocaleDateString()})</strong>
                    </p>
                    <button
                      type="button"
                      onClick={() => setFormState(prev => ({ ...prev, showDatePicker: true }))}
                      style={{
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      📆 Schedule for Another Date
                    </button>
                  </div>
                ) : (
                  <div>
                    <label className="section-label">
                      Select Date <span className="required">*</span>
                    </label>
                    <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                      <input
                        type="date"
                        value={formState.selectedDate}
                        onChange={(e) => {
                          const validation = InputValidator.validateDate(e.target.value);
                          if (validation.valid) {
                            setFormState(prev => ({ ...prev, selectedDate: e.target.value }));
                          } else {
                            alert(validation.error);
                          }
                        }}
                        min={new Date().toISOString().split('T')[0]}
                        max={new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                        required
                        style={{
                          padding: '12px 15px',
                          border: '2px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '16px'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setFormState(prev => ({
                            ...prev,
                            showDatePicker: false,
                            selectedDate: new Date().toISOString().split('T')[0]
                          }));
                        }}
                        style={{
                          backgroundColor: '#6c757d',
                          color: 'white',
                          border: 'none',
                          padding: '10px 15px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        Book for Today
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {formState.selectedStudio && (
            <div className="form-group">
              <label className="section-label">
                Select Time Slot <span className="required">*</span>
                {dataState.isLoading && (
                  <span style={{color: '#007bff', fontSize: '12px', marginLeft: '10px'}}>
                    🔄 Refreshing availability...
                  </span>
                )}
              </label>
              <div style={{display: 'grid', gap: '10px', marginTop: '10px'}}>
                {TIME_SLOTS.map(slot => {
                  const slotData = dataState.slotsData[formState.selectedStudio]?.[slot.id];
                  const bookedCount = slotData?.bookedCount || 0;
                  
                  // Enhanced slot validation with real-time checks
                  const now = new Date();
                  const selectedDateObj = new Date(formState.selectedDate);
                  const isToday = selectedDateObj.toDateString() === now.toDateString();
                  
                  let isPastSlot = false;
                  let wouldExceedClosing = false;
                  
                  if (isToday) {
                    const currentTime = now.getHours() * 60 + now.getMinutes();
                    const slotEndTime = parseInt(slot.id.split('-')[1].split(':')[0]) * 60 + parseInt(slot.id.split('-')[1].split(':')[1]);
                    const slotStartTime = parseInt(slot.id.split('-')[0].split(':')[0]) * 60 + parseInt(slot.id.split('-')[0].split(':')[1]);
                    
                    // Only block if the slot has completely ended
                    if (currentTime >= slotEndTime) {
                      isPastSlot = true;
                    }
                    
                    // Check if the next person's estimated time would exceed the slot end or 5 PM
                    if (!isPastSlot) {
                      // For a started slot, estimated time starts from now; for future slot, from slot start
                      const effectiveStart = Math.max(currentTime, slotStartTime);
                      const nextPersonWaitTime = bookedCount * 3; // 3 min per person
                      const estimatedArrivalTime = effectiveStart + nextPersonWaitTime;
                      
                      if (estimatedArrivalTime >= slotEndTime) {
                        wouldExceedClosing = true;
                      }
                      if (estimatedArrivalTime >= 1020) { // 5 PM
                        wouldExceedClosing = true;
                      }
                    }
                  }
                  
                  const isSlotFull = bookedCount >= 20;
                  const dayOfWeek = selectedDateObj.getDay();
                  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                  const isHoliday = dataState.holidays.includes(formState.selectedDate);
                  const isAvailable = !isPastSlot && !isSlotFull && !isWeekend && !isHoliday && !wouldExceedClosing;
                  
                  return (
                    <div
                      key={slot.id}
                      onClick={() => isAvailable && setFormState(prev => ({ ...prev, selectedSlot: slot.id }))}
                      style={{
                        padding: '15px',
                        border: formState.selectedSlot === slot.id ? '2px solid #007bff' : '1px solid #ddd',
                        borderRadius: '6px',
                        cursor: isAvailable ? 'pointer' : 'not-allowed',
                        backgroundColor: isAvailable ? '#fff' : '#f8f9fa',
                        opacity: isAvailable ? 1 : 0.6,
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <span style={{fontWeight: 'bold'}}>{slot.label}</span>
                        <span style={{
                          color: isAvailable ? '#28a745' : '#dc3545',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}>
                          {bookedCount}/20 booked
                        </span>
                      </div>
                      {isWeekend && (
                        <div style={{color: '#dc3545', fontSize: '12px', marginTop: '5px'}}>
                          Studio Closed (Weekend)
                        </div>
                      )}
                      {isHoliday && (
                        <div style={{color: '#dc3545', fontSize: '12px', marginTop: '5px'}}>
                          Studio Closed (Holiday)
                        </div>
                      )}
                      {!isWeekend && !isHoliday && isPastSlot && (
                        <div style={{color: '#dc3545', fontSize: '12px', marginTop: '5px'}}>
                          Slot has ended
                        </div>
                      )}
                      {!isWeekend && !isHoliday && !isPastSlot && isSlotFull && (
                        <div style={{color: '#dc3545', fontSize: '12px', marginTop: '5px'}}>
                          Slot Full
                        </div>
                      )}
                      {!isWeekend && !isHoliday && !isPastSlot && !isSlotFull && wouldExceedClosing && (
                        <div style={{color: '#dc3545', fontSize: '12px', marginTop: '5px'}}>
                          Queue full — estimated time exceeds slot window
                        </div>
                      )}
                      {slotData?.lastUpdated && (
                        <div style={{color: '#6c757d', fontSize: '10px', marginTop: '5px'}}>
                          Updated: {new Date(slotData.lastUpdated).toLocaleTimeString()}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {dataState.existingBooking && (
            <div style={{
              color: '#856404',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffeaa7',
              borderRadius: '4px',
              padding: '15px',
              margin: '15px 0'
            }}>
              <div style={{fontWeight: 'bold', marginBottom: '8px'}}>⚠️ Existing Booking Found</div>
              <div>You already have a booking today:</div>
              <div style={{marginTop: '5px'}}>
                <strong>Token:</strong> {dataState.existingBooking.tokenNumber}<br/>
                <strong>Studio:</strong> {dataState.existingBooking.siteCode}<br/>
                <strong>Status:</strong> {dataState.existingBooking.status}
              </div>
              <div style={{marginTop: '8px', fontSize: '14px'}}>
                Please complete your existing booking before making a new one.
              </div>
            </div>
          )}

          {submissionState.bookingError && (
            <div style={{
              color: '#dc3545',
              backgroundColor: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '4px',
              padding: '15px',
              margin: '15px 0'
            }}>
              <div style={{fontWeight: 'bold', marginBottom: '5px'}}>⚠️ Booking Error</div>
              <div>{submissionState.bookingError}</div>
              {submissionState.retryCount > 0 && (
                <div style={{fontSize: '12px', marginTop: '5px', color: '#6c757d'}}>
                  Attempt #{submissionState.retryCount} - Last tried: {
                    submissionState.lastAttemptTime ? 
                    new Date(submissionState.lastAttemptTime).toLocaleTimeString() : 
                    'Unknown'
                  }
                </div>
              )}
            </div>
          )}

          {submissionState.isSubmitting && (
            <div style={{
              color: '#007bff', 
              textAlign: 'center', 
              margin: '15px 0',
              padding: '15px',
              backgroundColor: '#e7f3ff',
              border: '1px solid #b3d9ff',
              borderRadius: '4px'
            }}>
              <div style={{fontWeight: 'bold', marginBottom: '5px'}}>🔄 Processing Your Booking...</div>
              <div style={{fontSize: '14px'}}>Please wait while we secure your slot</div>
            </div>
          )}

          <div className="button-group">
            <button 
              type="submit" 
              disabled={
                submissionState.isSubmitting || 
                !formState.selectedStudio || 
                !formState.selectedSlot || 
                validationState.emailError || 
                !formState.email || 
                dataState.existingBooking ||
                validationState.formErrors.length > 0
              }
              style={{
                opacity: (submissionState.isSubmitting || 
                         !formState.selectedStudio || 
                         !formState.selectedSlot || 
                         validationState.emailError || 
                         !formState.email || 
                         dataState.existingBooking ||
                         validationState.formErrors.length > 0) ? 0.6 : 1
              }}
            >
              {submissionState.isSubmitting ? 
                '🔄 Booking...' : 
                dataState.existingBooking ? 
                'Existing Booking Found' : 
                '📸 Book Photo Session'
              }
            </button>
            <button 
              type="button" 
              onClick={() => window.open('/check-booking', '_blank')}
              style={{
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                marginLeft: '10px'
              }}
            >
              🔍 Check My Booking
            </button>
          </div>
        </form>

        {/* Enhanced status information */}
        {dataState.lastRefresh && (
          <div style={{
            textAlign: 'center',
            marginTop: '20px',
            padding: '10px',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#6c757d'
          }}>
            <div>Last updated: {new Date(dataState.lastRefresh).toLocaleTimeString()}</div>
            <div>Auto-refresh: Every 30 seconds</div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}

export default PhotoStudioFormEnhanced;